import type { ICardProps, IOmit } from '@sixui/ui';
import { Card, CardMedia, Text } from '@sixui/ui';
import { colorSchemeTokens } from '@sixui/ui/themes/base/colorScheme.stylex';
import { outlineTokens } from '@sixui/ui/themes/base/outline.stylex';
import stylex from '@stylexjs/stylex';

import type { IContainerProps } from '~/components/utils/Container';

export type IFileGalleryDialogFigureProps = IContainerProps &
  IOmit<ICardProps, 'children'> & {
    src?: string;
    alt?: string;
    title?: string;
    description?: string;
    selected?: boolean;
  };

const styles = stylex.create({
  host: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    gridTemplateRows: 'minmax(0, 1fr)',
    gridAutoFlow: 'dense',
  },
  image: {
    gridRow: '1 / -1',
    gridColumn: '1 / -1',
    inlineSize: '100%',
    blockSize: '100%',
    objectFit: 'cover',
    borderRadius: 'inherit',
  },
  caption: {
    gridRow: '1 / -1',
    gridColumn: '1 / -1',
    alignSelf: 'end',
    padding: '8px 15% 8px 8px',
    zIndex: 1,
    background: 'linear-gradient(transparent 25%, rgb(0 0 0 / 0.75))',
    color: '#fff',
    borderRadius: 'inherit',
  },
  description: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  selected: {
    outlineWidth: outlineTokens.width$md,
    outlineStyle: 'solid',
    outlineColor: colorSchemeTokens.primary,
    outlineOffset: `calc(-1 * ${outlineTokens.width$md})`,
  },
});

export const FileGalleryDialogFigure: React.FC<
  IFileGalleryDialogFigureProps
> = (props) => {
  const { sx, src, alt, title, description, selected, ...other } = props;

  return (
    <Card sx={[styles.host, selected && styles.selected, sx]} {...other}>
      {src && (
        <CardMedia sx={styles.image}>
          <img {...stylex.props(styles.image)} src={src} alt={alt} />
        </CardMedia>
      )}
      {(title || description) && (
        <figcaption {...stylex.props(styles.caption)}>
          {title && (
            <Text variant="label" size="md" truncateLines={3}>
              {title}
            </Text>
          )}
          {description && (
            <Text variant="body" size="md" as="p" sx={styles.description}>
              Description
            </Text>
          )}
        </figcaption>
      )}
    </Card>
  );
};
